/*
 * CRAVE scroll motion: reveals (IntersectionObserver, play once) and scroll-linked
 * effects (one requestAnimationFrame loop). Only transform and opacity are animated.
 * Strengths live as CSS variables at the top of crave-motion.css.
 *
 * Sections are found by their key in the JSON template (the id suffix), so the keys
 * hero, flavor, story, faq, cta, founders and longform must keep their names.
 */
(() => {
  const root = document.documentElement;

  if (
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    !('IntersectionObserver' in window) ||
    (window.Shopify && window.Shopify.designMode)
  ) {
    return;
  }

  /** @param {string} key */
  const section = (key) => document.querySelector(`.shopify-section[id$="__${key}"]`);
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);

  /* ---- Tunable strengths ---- */

  const vars = {};

  const readVars = () => {
    const styles = getComputedStyle(root);
    /** Returns px, deg, ms or a plain number depending on the unit used. */
    const read = (name) => {
      const raw = styles.getPropertyValue(name).trim();
      const number = parseFloat(raw) || 0;
      if (raw.endsWith('vw')) return (number * window.innerWidth) / 100;
      if (raw.endsWith('ms')) return number;
      if (/[\d.]s$/.test(raw)) return number * 1000;
      return number;
    };

    vars.lineStagger = read('--cm-line-stagger');
    vars.textStagger = read('--cm-text-stagger');
    vars.cardStagger = read('--cm-card-stagger');
    vars.rowStagger = read('--cm-row-stagger');
    vars.storyRotateFrom = read('--cm-story-rotate-from');
    vars.storyRotateTo = read('--cm-story-rotate-to');
    vars.storyRise = read('--cm-story-rise');
    vars.storyScaleFrom = read('--cm-story-scale-from');
    vars.heroWord = read('--cm-hero-word-parallax');
    vars.heroCan = read('--cm-hero-can-parallax');
    vars.flavorShift = read('--cm-flavor-shift');
    vars.ctaRise = read('--cm-cta-rise');
    vars.ctaWordRise = read('--cm-cta-word-rise');
  };

  readVars();

  /* ---- One animation loop for everything scroll-linked ---- */

  /** @type {Array<(viewportHeight: number) => void>} */
  const linked = [];
  let scheduled = false;

  const frame = () => {
    scheduled = false;
    const viewportHeight = window.innerHeight;

    for (const update of linked) update(viewportHeight);
  };

  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(frame);
  };

  // On desktop the theme scrolls inside .page-wrapper, on phones the window scrolls.
  const pageWrapper = document.querySelector('.page-wrapper');
  let wrapperScrolls = false;

  const findScroller = () => {
    wrapperScrolls = Boolean(pageWrapper) && /auto|scroll/.test(getComputedStyle(pageWrapper).overflowY);
  };

  const scrollTop = () => (wrapperScrolls ? pageWrapper.scrollTop : window.scrollY);
  const scrollMax = () =>
    wrapperScrolls
      ? pageWrapper.scrollHeight - pageWrapper.clientHeight
      : root.scrollHeight - window.innerHeight;

  findScroller();
  window.addEventListener('scroll', schedule, { passive: true });
  if (pageWrapper) pageWrapper.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', () => {
    readVars();
    findScroller();
    schedule();
  });

  /* ---- Reveals: play once when the element enters the viewport ---- */

  /** @type {Map<Element, (delay: number) => void>} */
  const plays = new Map();
  /** @type {Map<Element, { group: string, stagger: number }>} */
  const groups = new Map();

  const observer = new IntersectionObserver(
    (entries) => {
      /** @type {Record<string, number>} */
      const batch = {};

      for (const entry of entries) {
        // Also play anything already above the viewport (page loaded part-way down).
        const isAbove = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        if (!entry.isIntersecting && !isAbove) continue;

        const element = entry.target;
        observer.unobserve(element);

        let delay = 0;
        const group = groups.get(element);
        if (group && !isAbove) {
          batch[group.group] = (batch[group.group] || 0) + 1;
          delay = (batch[group.group] - 1) * group.stagger;
        }

        plays.get(element)?.(delay);
      }
    },
    { rootMargin: '0px 0px -12% 0px', threshold: 0.15 }
  );

  /**
   * @param {Element} element
   * @param {(delay: number) => void} play
   * @param {{ group: string, stagger: number }} [group] Elements entering together are staggered.
   */
  const onEnter = (element, play, group) => {
    plays.set(element, play);
    if (group) groups.set(element, group);
    observer.observe(element);
  };

  /** @param {HTMLElement} element */
  const show = (element, delay = 0) => {
    element.style.setProperty('--cm-delay', `${delay}ms`);
    element.classList.add('cm-in');
  };

  /**
   * Wraps each word of a headline so it can be revealed line by line. Lines are
   * worked out when the reveal plays, so wrapping stays responsive.
   * @param {HTMLElement} heading
   */
  const splitWords = (heading) => {
    const words = [];
    const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);

    for (const node of textNodes) {
      const fragment = document.createDocumentFragment();
      for (const part of (node.textContent || '').split(/(\s+)/)) {
        if (part === '') continue;
        if (/^\s+$/.test(part)) {
          fragment.append(' ');
          continue;
        }
        const word = document.createElement('span');
        word.className = 'cm-word';
        word.textContent = part;
        fragment.append(word);
        words.push(word);
      }
      node.replaceWith(fragment);
    }

    heading.classList.add('cm-lines');
    return words;
  };

  /**
   * @param {HTMLElement} heading
   * @param {HTMLElement[]} words
   * @returns {number} How long the line stagger runs, in ms.
   */
  const playLines = (heading, words, delay = 0) => {
    let line = -1;
    let lastTop = null;

    for (const word of words) {
      const top = word.offsetTop;
      if (lastTop === null || Math.abs(top - lastTop) > 2) {
        line++;
        lastTop = top;
      }
      word.style.setProperty('--cm-line', String(line));
    }

    show(heading, delay);
    return Math.max(line, 0) * vars.lineStagger;
  };

  /**
   * Headline line by line, then the given elements fade in after it.
   * @param {HTMLElement | null} heading
   * @param {HTMLElement[]} [followers]
   */
  const revealHeadline = (heading, followers = []) => {
    if (!heading) return;
    const words = splitWords(heading);
    for (const follower of followers) follower.classList.add('cm-fade');

    onEnter(heading, (delay) => {
      const linesDone = delay + playLines(heading, words, delay) + 200;
      followers.forEach((follower, index) => show(follower, linesDone + index * vars.textStagger));
    });
  };

  /**
   * @param {HTMLElement[]} elements
   * @param {string} group
   * @param {number} stagger
   * @param {string} [className]
   */
  const revealGroup = (elements, group, stagger, className = 'cm-reveal') => {
    for (const element of elements) {
      element.classList.add(className);
      onEnter(element, (delay) => show(element, delay), { group, stagger });
    }
  };

  /** @returns {HTMLElement[]} */
  const all = (parent, selector) => (parent ? Array.from(parent.querySelectorAll(selector)) : []);

  /* ---- Story section: scroll-linked can, headline by line, paragraphs fade ---- */

  const story = section('story');
  if (story) {
    const heading = story.querySelector('h2');
    revealHeadline(heading, all(story, '.text-block p, a.button'));

    const block = story.querySelector('.image-block');
    const image = block?.querySelector('img');
    if (block && image) {
      const shadow = document.createElement('div');
      shadow.className = 'cm-can__shadow';
      block.classList.add('cm-can');
      story.classList.add('cm-clip');
      block.prepend(shadow);

      linked.push((viewportHeight) => {
        const rect = block.getBoundingClientRect();
        if (rect.bottom < -100 || rect.top > viewportHeight + 100) return;

        // 0 as the can enters at the bottom of the screen, 1 as it leaves at the top.
        const progress = clamp((viewportHeight - rect.top) / (viewportHeight + rect.height));
        const landed = easeOut(clamp(progress / 0.5));
        const rotate = vars.storyRotateFrom + (vars.storyRotateTo - vars.storyRotateFrom) * progress;
        const y = vars.storyRise * (1 - landed);
        const scale = vars.storyScaleFrom + (1 - vars.storyScaleFrom) * landed;
        const sway = -Math.sin((rotate * Math.PI) / 180) * rect.height * 0.45;

        image.style.transform = `translate3d(0, ${y}px, 0) rotate(${rotate}deg) scale(${scale})`;
        shadow.style.transform = `translate3d(${sway}px, ${y}px, 0) scale(${1.6 - 0.6 * landed}, 1)`;
        shadow.style.opacity = String(0.1 + 0.3 * landed);
      });
    }
  }

  /* ---- Hero stage: backdrop word lags, can runs ahead ---- */

  const hero = document.querySelector('.can-stage--hero');
  if (hero) {
    const word = hero.querySelector('.can-stage__word');
    const can = hero.querySelector('.can-stage__can-box');

    linked.push(() => {
      const scrolled = Math.min(scrollTop(), hero.offsetHeight);
      if (word) word.style.transform = `translate3d(0, ${scrolled * vars.heroWord}px, 0)`;
      if (can) can.style.transform = `translate3d(0, ${-scrolled * vars.heroCan}px, 0)`;
    });
  }

  /* ---- Flavor band: jumbo lines slide in from opposite sides ---- */

  const flavor = section('flavor');
  const jumbo = flavor?.querySelector('jumbo-text');
  if (flavor && jumbo) {
    const lines = all(jumbo, '.jumbo-text-line');
    flavor.classList.add('cm-clip');

    linked.push((viewportHeight) => {
      const rect = jumbo.getBoundingClientRect();
      if (rect.bottom < -100 || rect.top > viewportHeight + 100) return;

      // Settled once the middle of the jumbo text reaches the middle of the screen.
      const progress = easeOut(clamp((viewportHeight - rect.top) / (viewportHeight / 2 + rect.height / 2)));
      const shift = vars.flavorShift * (1 - progress);

      lines.forEach((line, index) => {
        line.style.transform = `translate3d(${index % 2 === 0 ? -shift : shift}px, 0, 0)`;
        line.style.opacity = String(0.2 + 0.8 * progress);
      });
    });
  }

  /* ---- FAQ: heading by line, rows fade in ---- */

  const faq = section('faq');
  if (faq) {
    revealHeadline(faq.querySelector('h2'));
    revealGroup(all(faq, 'details'), 'faq', vars.rowStagger, 'cm-fade');
  }

  /* ---- Closing stage: can rises from the bottom edge, backdrop fades up ---- */

  for (const stage of document.querySelectorAll('.can-stage--cropped')) {
    const visual = stage.querySelector('.can-stage__visual');
    const word = stage.querySelector('.can-stage__word');
    const can = stage.querySelector('.can-stage__can-box');
    if (!visual) continue;

    linked.push((viewportHeight) => {
      const rect = visual.getBoundingClientRect();
      if (rect.bottom < -150 || rect.top > viewportHeight + 150) return;

      // Finish within the scroll that is actually left: this stage sits at the end of the page.
      const topAtPageEnd = rect.top - (scrollMax() - scrollTop());
      const travel = Math.max(1, Math.min(rect.height * 0.9, (viewportHeight - topAtPageEnd) * 0.9));
      const progress = easeOut(clamp((viewportHeight - rect.top) / travel));
      if (can) can.style.transform = `translate3d(0, ${vars.ctaRise * (1 - progress)}px, 0)`;
      if (word) {
        word.style.transform = `translate3d(0, ${vars.ctaWordRise * (1 - progress)}px, 0)`;
        word.style.opacity = String(progress);
      }
    });
  }

  /* ---- Our Story page: same reveal styles ---- */

  const founders = section('founders');
  if (founders) {
    const storyHero = section('hero');
    if (storyHero) revealHeadline(storyHero.querySelector('h1'), all(storyHero, '.text-block p'));

    revealHeadline(founders.querySelector('h2'));
    revealGroup(
      all(founders, '.section-content-wrapper > .group-block > .group-block-content > .group-block'),
      'founders',
      vars.cardStagger
    );
  }

  const longform = section('longform');
  if (longform) revealHeadline(longform.querySelector('h2'), all(longform, '.text-block p'));

  schedule();
})();
